import { useState } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { MINUTE_STEP, snapMinutes, splitDuration, stepDuration } from '@/utils/volunteerHours';

export type DurationStepperProps = {
  /** Total minutes. */
  value: number;
  onChange: (minutes: number) => void;
  min?: number;
  max?: number;
  /** error = the red styling used for removing hours. */
  tone?: 'primary' | 'error';
  /** sm fits the compact org review cards. */
  size?: 'md' | 'sm';
  disabled?: boolean;
};

const HOURS_DRAFT = /^\d{0,3}$/;
const MINUTES_DRAFT = /^\d{0,2}$/;

/**
 * md must fit both groups on one row inside the narrowest host: the 90%-width dialogs on a 360px
 * phone leave 284px. Each md group is 44 + 2 + 36 + 2 + 44 = 128px; two plus the 16px gap = 272px.
 */
const SIZES = {
  md: { button: '44px', radius: 'xl', buttonFont: 'xl', numberFont: '24px', numberWidth: '36px', gap: '0.5', between: '4' },
  sm: { button: '28px', radius: 'lg', buttonFont: 'lg', numberFont: '18px', numberWidth: '36px', gap: '2', between: '3' },
} as const;

/**
 * Hours and minutes side by side, each with −/+ buttons. Minutes move in MINUTE_STEP steps and roll
 * over into hours. Both numbers can be typed: focusing a field empties it (the current number stays
 * as the placeholder) so typing replaces rather than appends, leaving it empty keeps the value, and
 * typed minutes snap to the step on blur.
 */
export function DurationStepper({
  value,
  onChange,
  min = MINUTE_STEP,
  max,
  tone = 'primary',
  size = 'md',
  disabled = false,
}: DurationStepperProps) {
  const [hoursDraft, setHoursDraft] = useState<string | null>(null);
  const [minutesDraft, setMinutesDraft] = useState<string | null>(null);
  const { hours, minutes } = splitDuration(value);
  const s = SIZES[size];
  const color = tone === 'error' ? 'volyx.error' : 'volyx.primary';

  // Hour steps clamp to the bound (1h 00 − 1h lands on 0h 05) so going under an hour never greys
  // out; minute steps refuse to cross it, which only happens when already at the bound.
  const stepped = (delta: number) =>
    stepDuration(value, delta, { min, max, clamp: Math.abs(delta) === 60 });
  const step = (delta: number) => {
    const next = stepped(delta);
    if (next !== value) onChange(next);
  };

  const commit = () => {
    if (!hoursDraft && !minutesDraft) {
      setHoursDraft(null);
      setMinutesDraft(null);
      return;
    }
    const h = hoursDraft ? Number(hoursDraft) : hours;
    const m = minutesDraft ? snapMinutes(Number(minutesDraft)) : minutes;
    let total = Math.max(min, h * 60 + m);
    if (max !== undefined) total = Math.min(max, total);
    setHoursDraft(null);
    setMinutesDraft(null);
    if (total !== value) onChange(total);
  };

  const stepButton = (label: string, delta: number, glyph: string) => (
    <chakra.button
      type="button"
      aria-label={label}
      disabled={disabled || stepped(delta) === value}
      onClick={() => step(delta)}
      w={s.button}
      h={s.button}
      minW={s.button}
      borderRadius={s.radius}
      bg="volyx.lightGray"
      border="none"
      cursor="pointer"
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontSize={s.buttonFont}
      fontWeight="bold"
      color="volyx.text"
      flexShrink={0}
      _hover={{ bg: 'volyx.mediumGray' }}
      _disabled={{ opacity: 0.4, cursor: 'not-allowed', _hover: { bg: 'volyx.lightGray' } }}
    >
      {glyph}
    </chakra.button>
  );

  const numberField = (
    label: string,
    draft: string | null,
    shown: string,
    unit: string,
    onText: (next: string) => void
  ) => (
    <Box textAlign="center" w={s.numberWidth} flexShrink={0}>
      <chakra.input
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={draft ?? shown}
        placeholder={shown}
        disabled={disabled}
        onFocus={() => onText('')}
        onChange={(e) => onText(e.target.value)}
        onBlur={commit}
        w="100%"
        textAlign="center"
        fontSize={s.numberFont}
        fontWeight="bold"
        lineHeight="1.2"
        border="none"
        outline="none"
        bg="transparent"
        color={color}
        p="0"
        _placeholder={{ color: 'volyx.textSecondary', opacity: 0.6 }}
      />
      <Text fontSize="xs" color="volyx.textSecondary" m="0">
        {unit}
      </Text>
    </Box>
  );

  return (
    <Flex align="center" justify="center" gap={s.between} wrap="wrap">
      <Flex align="center" gap={s.gap}>
        {stepButton('Decrease hours', -60, '−')}
        {numberField('Hours', hoursDraft, String(hours), hours === 1 ? 'hour' : 'hours', (next) => {
          if (HOURS_DRAFT.test(next)) setHoursDraft(next);
        })}
        {stepButton('Increase hours', 60, '+')}
      </Flex>
      <Flex align="center" gap={s.gap}>
        {stepButton('Decrease minutes', -MINUTE_STEP, '−')}
        {numberField('Minutes', minutesDraft, String(minutes).padStart(2, '0'), 'min', (next) => {
          if (MINUTES_DRAFT.test(next) && (next === '' || Number(next) <= 59)) setMinutesDraft(next);
        })}
        {stepButton('Increase minutes', MINUTE_STEP, '+')}
      </Flex>
    </Flex>
  );
}
